import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { analyticsService, tasksService, usersService } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, TrendingDown, Users, FolderKanban, CheckCircle, Clock, Plus, Building2, ArrowRight } from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

const COLORS = ["#3B82F6", "#16A34A", "#F59E0B", "#DC2626", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#6366F1"];

const DEPT_COLORS = {
  "Social Media Ambassadors": "#3B82F6",
  "HR": "#16A34A",
  "Email Marketing": "#F59E0B",
  "Client Relations": "#DC2626",
  "Creativity": "#8B5CF6",
  "Content Creation": "#EC4899",
  "Public Relations": "#14B8A6",
  "Digital Marketing": "#F97316",
  "Executive": "#6366F1"
};

const getDeptIcon = (dept) => {
  const icons = {
    "Executive": "👔",
    "Digital Marketing": "📊",
    "Social Media Ambassadors": "📱",
    "HR": "🤝",
    "Email Marketing": "📧",
    "Client Relations": "💼",
    "Creativity": "🎨",
    "Content Creation": "✍️",
    "Public Relations": "📢"
  };
  return icons[dept] || "🏢";
};

const Dashboard = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentTasks, setRecentTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, tasksData, usersData] = await Promise.all([
          analyticsService.getDashboardStats(),
          tasksService.getAll(),
          usersService.getAll()
        ]);
        setStats(statsData);
        setRecentTasks(tasksData.slice(0, 5));
        setTeamMembers(usersData);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const departmentData = stats ? Object.entries(stats.departments).map(([name, value]) => ({
    name: name.length > 12 ? name.substring(0, 12) + "…" : name,
    fullName: name,
    value
  })) : [];

  const taskStatusData = stats ? [
    { name: "Pending", value: stats.taskStats.pending },
    { name: "In Progress", value: stats.taskStats.inProgress },
    { name: "Completed", value: stats.taskStats.completed }
  ] : [];

  // Department breakdown for the team overview card
  const deptBreakdown = {};
  teamMembers.forEach(user => {
    const dept = user.department || "Unassigned";
    if (!deptBreakdown[dept]) deptBreakdown[dept] = { total: 0, leads: 0 };
    deptBreakdown[dept].total++;
    if (user.role?.includes("Lead") || user.role === "COO" || user.role?.includes("Manager")) {
      deptBreakdown[dept].leads++;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-action-blue" />
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-navy">Dashboard</h1>
          <p className="text-gray-500">Welcome back, {userData?.name || "User"}!</p>
        </div>
        <Button
          onClick={() => navigate("/intake?new=true")}
          className="bg-navy hover:bg-navy-light text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Intake
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-t-4 border-t-action-blue">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Team Members</p>
                <p className="text-3xl font-bold text-navy">{stats?.totalUsers || 0}</p>
                {stats?.hasData && stats?.deltas?.users ? (
                  <p className={`text-xs flex items-center mt-1 ${stats.deltas.users.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                    {stats.deltas.users.startsWith('+') ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                    {stats.deltas.users} this month
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">—</p>
                )}
              </div>
              <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center">
                <Users className="w-7 h-7 text-action-blue" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-green">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Active Projects</p>
                <p className="text-3xl font-bold text-green">{stats?.activeProjects || 0}</p>
                {stats?.hasData && stats?.deltas?.projects ? (
                  <p className={`text-xs flex items-center mt-1 ${stats.deltas.projects.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                    {stats.deltas.projects.startsWith('+') ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                    {stats.deltas.projects} this month
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">—</p>
                )}
              </div>
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center">
                <FolderKanban className="w-7 h-7 text-green" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-amber">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Pending Tasks</p>
                <p className="text-3xl font-bold text-amber">{stats?.taskStats.pending || 0}</p>
                {stats?.hasData && stats?.deltas?.pending ? (
                  <p className={`text-xs flex items-center mt-1 ${stats.deltas.pending.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                    {stats.deltas.pending.startsWith('+') ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                    {stats.deltas.pending} vs completed
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">—</p>
                )}
              </div>
              <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center">
                <Clock className="w-7 h-7 text-amber" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-purple">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Completed Tasks</p>
                <p className="text-3xl font-bold text-purple-600">{stats?.taskStats.completed || 0}</p>
                {stats?.deltas?.completed ? (
                  <p className={`text-xs flex items-center mt-1 ${stats.deltas.completed.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                    {stats.deltas.completed.startsWith('+') ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                    {stats.deltas.completed} this week
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">—</p>
                )}
              </div>
              <div className="w-14 h-14 bg-purple-50 rounded-full flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Tasks by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={taskStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {taskStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Team Members by Department</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={departmentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={11} angle={-20} textAnchor="end" height={50} />
                  <YAxis fontSize={12} />
                  <Tooltip 
                    formatter={(value, name) => [value, "Members"]}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {departmentData.map((entry, index) => (
                      <Cell key={`bar-${index}`} fill={DEPT_COLORS[entry.fullName] || COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Department Overview + Recent Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Department Overview */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Department Overview
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/team")} className="text-action-blue">
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(deptBreakdown)
                .sort((a, b) => b[1].total - a[1].total)
                .map(([dept, info]) => (
                  <div key={dept} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{getDeptIcon(dept)}</span>
                      <div>
                        <p className="font-medium text-navy text-sm">{dept}</p>
                        <p className="text-xs text-gray-500">{info.leads} lead{info.leads !== 1 ? "s" : ""}</p>
                      </div>
                    </div>
                    <Badge className="bg-blue-50 text-blue-700">
                      {info.total} member{info.total !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                ))}
              {Object.keys(deptBreakdown).length === 0 && (
                <p className="text-center text-gray-400 py-4">No team data yet — seed users on the Team page</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Tasks */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            {recentTasks.length > 0 ? (
              <div className="space-y-3">
                {recentTasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-navy">{task.title || "Untitled Task"}</p>
                      <p className="text-sm text-gray-500">{task.department}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      task.status === "completed" ? "bg-green-100 text-green-700" :
                      task.status === "inProgress" ? "bg-blue-100 text-blue-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>
                      {task.status || "pending"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-400 py-8">No tasks found</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;